import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSortModule, MatSort, SortDirection } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { Subject, takeUntil, combineLatest, distinctUntilChanged } from 'rxjs';

import { DataTableConfig } from './data-table-config.interface';
import { DataTableSource } from './data-source/data-source.types';
import { DataSourceAdapter } from './data-source/data-source-adapter.interface';
import { isPaginatedDataService, resolveDataSource } from './data-source/data-source.resolver';
import { PaginationState } from '../../services/paginated-data.service';
import { TableSelectionService } from '../../services/table-selection.service';
import { SelectionService } from '../../services/selection.service';
import { MenuOverlayService } from '../../services/menu-overlay.service';
import { TableService } from '../tables/tables.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ColumnConfig } from '../field-renderer/field-config.interface';
import { TableColumnsProp } from '../../models/tables.model';
import {
  applySavedColumnOrder,
  loadGridColumnPreferences,
  PersistedGridColumnPreferences,
  reorderColumnsAfterHeaderDrop,
  saveGridColumnPreferences,
  sortTableColumnPropsByVisibleOrder,
} from '../../utility/grid-column-preferences.util';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { KeyStrokeDirectivesModule } from '../../directives/keystroke/keystrokes.module';


@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatDividerModule,
    MatIconModule,
    DragDropModule,
    SvgIconComponent,
    KeyStrokeDirectivesModule,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableSelectionService],
})
export class DataTableComponent<TRow = unknown> implements AfterViewInit, OnChanges {
  @Input({ required: true }) config!: DataTableConfig<TRow>;

  @Input({ required: true }) dataSource!: DataTableSource<TRow>;

  @Input()
  set dataSearched(value: boolean) {
    this._dataSearched = value;
    this.updateEmptyMessage();
  }
  get dataSearched(): boolean {
    return this._dataSearched;
  }
  private _dataSearched = false;

  @Input() disablePageSize = false;

  /** Server-side total row count when pagination is enabled without PaginatedDataService. */
  @Input() externalTotalCount?: number;

  @Output() rowClick = new EventEmitter<{ row: TRow; column: string; linkRoute?: string }>();


  @Output() dashboardShortcutClick = new EventEmitter<{ row: TRow; column: string; linkRoute?: string }>();

  @Output() actionButtonClick = new EventEmitter<{ row: TRow; column: string }>();

  @Output() rowActionClick = new EventEmitter<{ row: TRow; action: string }>();

  @Output() selectionChange = new EventEmitter<void>();

  @Output() pageChange = new EventEmitter<PageEvent>();

   @Output() sortChange = new EventEmitter<{ field: string; direction: SortDirection }>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  get actualPaginator(): MatPaginator {
    return this.paginator;
  }

  get paginatorShown(): boolean {
    return (
      !!this.config?.features?.pagination &&
      this.dataSearched &&
      (this.matDataSource.data.length > 0 || this.totalCount > 0)
    );
  }
  get serverPagedGrid(): boolean {
    return isPaginatedDataService(this.dataSource);
  }

  /** Restores row checkboxes from root `SelectionService` (summary-table parity). */
  restoreSelections(): void {
    if (!this.config.features.selection || !this.rootSelectionService.hasAny()) {
      return;
    }

    const keyToRow = this.buildRowLookupMap();
    for (const key of this.rootSelectionService.getAllKeys()) {
      const row =
        keyToRow.get(key) ??
        keyToRow.get(String(key)) ??
        keyToRow.get(Number(key)) ??
        this.rootSelectionService.getRow(key);
      if (row) {
        this.selectionService.select(row);
      }
    }
    this.updateHeaderCheckboxState();
    this.selectionChange.emit();
    this.cdr.markForCheck();
  }

  /** Flat `select` + visible keys + `action` order for print/export. */
  getPrintDisplayColumns(): string[] {
    return structuredClone(this.displayedColumns);
  }

  matDataSource = new MatTableDataSource<TRow>();

  // Paginator state - bound directly in template
  currentPageIndex = 0;
  currentPageSize = 10;
  pageSizeOptions: number[] = [10, 25, 50];
  displayedColumns: string[] = [];
  visibleColumns: ColumnConfig<TRow>[] = [];

  private adapter!: DataSourceAdapter<TRow>;
  private readonly destroyRef = inject(DestroyRef);
  private readonly inputChange$ = new Subject<void>();
  private initialized = false;

  globalHeaderCheckboxState = false;
  indeterminate = false;
  totalCount = 0;
  isAllMode = false;
  isOverlayOpen = false;
  allItemsSelectedMessage = '';
  selectAllItemsMessage = '';
  emptyMessage = '';
  tableColumnProps: TableColumnsProp[] = [];
  tableSortDirection: SortDirection = '';
  private readonly sortShortcutNumberByKey = new Map<string, number>();

  selectionService = inject(TableSelectionService<TRow>);
  private readonly rootSelectionService = inject(SelectionService);
  private readonly menuOverlayService = inject(MenuOverlayService);
  private readonly tableService = inject(TableService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private columnPreferences!: PersistedGridColumnPreferences;

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;

    const configChanged = changes['config'] && !changes['config'].firstChange;
    const dataSourceChanged = changes['dataSource'] && !changes['dataSource'].firstChange;
    const disablePageSizeChanged = changes['disablePageSize'] && !changes['disablePageSize'].firstChange;

    if (configChanged) {
      this.updateEmptyMessage();
    }

    if (disablePageSizeChanged) {
      this.updatePageSizeOptions();
    }

    if (configChanged || dataSourceChanged) {
      this.reinitialize();
    }

    if (changes['externalTotalCount'] && !this.adapter?.totalCount$) {
      this.totalCount =
        this.externalTotalCount != null
          ? this.externalTotalCount
          : this.matDataSource.data.length;
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    this.initialize();
    this.initialized = true;

    this.destroyRef.onDestroy(() => {
      this.cleanup();
    });
  }

  private initialize(): void {
    this.selectionService.configure(this.config.rowIdField);
    this.adapter = resolveDataSource(this.dataSource);

    this.currentPageSize = this.config.defaultPagination?.pageSize || 10;
    this.currentPageIndex = 0;
    this.updatePageSizeOptions();

    // Initialize messages now that config is available
    this.updateEmptyMessage();
    this.updateSelectionMessages();

    this.setupColumns();
    this.setupSort();
    this.setupDataSubscription();
    this.setupSelectionSubscription();
  }

  private reinitialize(): void {
    this.cleanup();
    this.initialize();
  }

  private cleanup(): void {
    this.inputChange$.next();
    this.adapter?.disconnect();
  }

  private setupColumns(): void {
    this.columnPreferences = loadGridColumnPreferences(
      this.localStorageService,
      this.config.storageKey,
      this.config
    );
    const applied = applySavedColumnOrder(this.config, this.columnPreferences);
    this.tableColumnProps = applied.tableColumnProps;
    this.visibleColumns = applied.visibleColumns;
    this.rebuildDisplayedColumns();
    if (this.config.features.columnCustomization ?? true) {
      this.setupColumnChooser();
    }
  }

  private setupColumnChooser(): void {
    this.syncTableServiceForCustomizeMenu();

    this.tableService.columnVisibilityChanged
      .pipe(
        takeUntil(this.inputChange$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.refreshColumnsFromTableService();
      });
  }

  private rebuildDisplayedColumns(): void {
    const columns: string[] = [];

    if (this.config.features.selection) {
      columns.push('select');
    }

    columns.push(
      ...this.visibleColumns.map(col => String(col.key))
    );

    if (this.config.features.columnCustomization ?? true) {
      columns.push('action');
    }

    this.displayedColumns = columns;
    this.rebuildSortShortcutNumbers();
  }

  isSortShortcutColumn(column: ColumnConfig<TRow>): boolean {
    return (
      this.matDataSource.data.length > 0 &&
      !!this.config.features.sorting &&
      column.sortable !== false
    );
  }

  sortShortcutNumberForColumn(column: ColumnConfig<TRow>): number {
    return this.sortShortcutNumberByKey.get(String(column.key)) ?? 0;
  }

  private rebuildSortShortcutNumbers(): void {
    this.sortShortcutNumberByKey.clear();
    if (!this.config.features.sorting) {
      return;
    }
    let n = 1;
    for (const key of this.displayedColumns) {
      if (key === 'select' || key === 'action') {
        continue;
      }
      const col = this.visibleColumns.find(c => String(c.key) === key);
      if (!col || !this.isSortShortcutColumn(col)) {
        continue;
      }
      this.sortShortcutNumberByKey.set(key, n++);
    }
  }

  private setupSort(): void {
    if (!this.config.features.sorting) return;

    this.tableSortDirection = this.config.defaultPagination?.sortDirection || '';

    setTimeout(() => {
      if (!this.sort) return;

      const isServerSide = this.adapter.type === 'paginated';

      if (!isServerSide) {
        this.matDataSource.sort = this.sort;
      }

      this.sort.sortChange
        .pipe(
          takeUntil(this.inputChange$),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(sortState => {
          this.tableSortDirection = sortState.direction;
          this.cdr.markForCheck();

          if (isServerSide && this.adapter.sort && sortState.direction) {
            this.adapter.sort(sortState.active, sortState.direction);
          }
          this.sortChange.emit({ field: sortState.active, direction: sortState.direction });
        });
    });
  }

  private setupDataSubscription(): void {
    this.adapter
      .connect()
      .pipe(
        takeUntil(this.inputChange$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(data => {
        this.matDataSource.data = data;
        if (!this.adapter.totalCount$) {
          this.totalCount =
            this.externalTotalCount != null
              ? this.externalTotalCount
              : data.length;
        }
        this.rebuildSortShortcutNumbers();
        this.restoreSelections();
        this.updateHeaderCheckboxState();
        if (isPaginatedDataService(this.dataSource)) {
          this.applyServerPaginatorFromState(
            this.dataSource.currentPagination
          );
        }
        this.cdr.markForCheck();
      });

    if (this.adapter.totalCount$) {
      this.adapter.totalCount$
        .pipe(
          takeUntil(this.inputChange$),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(count => {
          this.totalCount = count;
          this.updateSelectionMessages();
          this.cdr.markForCheck();
        });
    }

    if (isPaginatedDataService(this.dataSource)) {
      this.dataSource.paginationState$
        .pipe(
          distinctUntilChanged(
            (a, b) =>
              a.fromIndex === b.fromIndex &&
              a.toIndex === b.toIndex &&
              a.sortingField === b.sortingField &&
              a.sortingOrder === b.sortingOrder
          ),
          takeUntil(this.inputChange$),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(state => {
          const span = state.toIndex - state.fromIndex;
          const pageSize =
            span > 0 ? span : (this.config.defaultPagination?.pageSize ?? 10);
          const pageIndex = span > 0 ? Math.floor(state.fromIndex / span) : 0;
          this.currentPageIndex = pageIndex;
          this.currentPageSize = pageSize;
          if (this.disablePageSize) {
            this.updatePageSizeOptions();
          }
          this.applyServerPaginatorFromState(state);
          this.cdr.markForCheck();
        });
    } else if (this.adapter.pageIndex$ && this.adapter.pageSize$) {
      combineLatest([this.adapter.pageIndex$, this.adapter.pageSize$])
        .pipe(
          distinctUntilChanged(
            ([pi, ps], [pi2, ps2]) => pi === pi2 && ps === ps2
          ),
          takeUntil(this.inputChange$),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(([pageIndex, pageSize]) => {
          this.currentPageIndex = pageIndex;
          this.currentPageSize = pageSize;
          if (this.disablePageSize) {
            this.updatePageSizeOptions();
          }
          this.cdr.markForCheck();
        });
    }
  }

  /** Align `MatPaginator` with `PaginatedDataService` after search/sort/breadcrumb (server-only template omits `[pageIndex]`/`[pageSize]`). */
  private applyServerPaginatorFromState(state: PaginationState): void {
    const span = state.toIndex - state.fromIndex;
    if (span <= 0) {
      return;
    }
    const pageIndex = Math.floor(state.fromIndex / span);
    const sync = () => {
      if (!this.paginator) {
        return;
      }
      if (this.paginator.pageSize !== span) {
        this.paginator.pageSize = span;
      }
      if (this.paginator.pageIndex !== pageIndex) {
        this.paginator.pageIndex = pageIndex;
      }
    };
    sync();
    setTimeout(sync);
  }

  private setupSelectionSubscription(): void {
    this.selectionService.state
      .pipe(
        takeUntil(this.inputChange$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(state => {
        this.isAllMode = state.mode === 'all';
        if (state.mode === 'none' || state.mode === 'all') {
          this.rootSelectionService.clear();
        }
        this.updateSelectionMessages();
        this.updateHeaderCheckboxState();
        this.selectionChange.emit();
        this.cdr.markForCheck();
      });
  }

  private updateSelectionMessages(): void {
    this.allItemsSelectedMessage = this.config.messages.allPageSelected(
      this.config.entityName,
      this.matDataSource.data.length
    );
    this.selectAllItemsMessage = this.config.messages.selectAll(
      this.config.entityName,
      this.totalCount
    );
  }

  /** @internal */
  onHeaderCheckboxChange(checked: boolean): void {
    if (this.isAllMode && !checked) {
      this.selectionService.clearSelection();
      return;
    }

    if (checked) {
      this.selectAllOnPage();
    } else {
      this.deselectAllOnPage();
    }
  }

  private selectAllOnPage(): void {
    this.matDataSource.data.forEach(row => {
      this.selectionService.select(row);
      if (this.selectionService.currentState.mode === 'partial') {
        this.cacheSelectedRow(row);
      }
    });
  }

  private deselectAllOnPage(): void {
    this.matDataSource.data.forEach(row => {
      this.selectionService.deselect(row);
      this.removeCachedRow(row);
    });
  }

  /** Stable radio group name for single-selection tables. */
  get selectionRadioGroupName(): string {
    return `${this.config.storageKey}-selection`;
  }

  /** Explicit opt-in: only HAWB routes selection grid sets this today. */
  get isSingleSelectionMode(): boolean {
    return this.config.features.singleSelection === true;
  }

  /** @internal */
  onRowSelect(row: TRow): void {
    this.selectionService.select(row);
    if (this.selectionService.currentState.mode === 'partial') {
      this.cacheSelectedRow(row);
    }
  }

  /** @internal - Radio selection: one row at a time, no header control. */
  onSingleRowSelect(row: TRow): void {
    if (this.isRowSelectionDisabled(row)) {
      return;
    }
    this.selectionService.clearSelection();
    this.selectionService.select(row);
    this.cacheSelectedRow(row);
  }

  /** @internal */
  onRowDeselect(row: TRow): void {
    this.selectionService.deselect(row);
    if (this.selectionService.currentState.mode === 'partial') {
      this.removeCachedRow(row);
    }
  }

  /** @internal */
  isRowSelected(row: TRow): boolean {
    return this.selectionService.isSelected(row);
  }

  /** @internal - Triggered by "Select all N items" link */
  onSelectAllClick(): void {
    this.selectionService.selectAll();
  }

  private updateHeaderCheckboxState(): void {
    if (!this.config.features.selection || this.isSingleSelectionMode) return;

    const totalOnPage = this.matDataSource.data.length;

    if (this.isAllMode) {
      const excludedOnPage = this.matDataSource.data.filter(row =>
        !this.selectionService.isSelected(row)
      ).length;

      if (excludedOnPage === 0) {
        this.globalHeaderCheckboxState = true;
        this.indeterminate = false;
      } else if (excludedOnPage === totalOnPage) {
        this.globalHeaderCheckboxState = false;
        this.indeterminate = false;
      } else {
        this.globalHeaderCheckboxState = false;
        this.indeterminate = true;
      }
      return;
    }

    const selectedCount = this.matDataSource.data.filter(row =>
      this.selectionService.isSelected(row)
    ).length;

    // All selected on current page
    if (selectedCount === totalOnPage && totalOnPage > 0) {
      this.globalHeaderCheckboxState = true;
      this.indeterminate = false;
      // Some but not all selected
    } else if (selectedCount > 0) {
      this.globalHeaderCheckboxState = false;
      this.indeterminate = true;
      // None selected (or empty page)
    } else {
      this.globalHeaderCheckboxState = false;
      this.indeterminate = false;
    }
  }

  /** Whether to show "All N items selected" / "Select all" banner */
  get showSelectAllBanner(): boolean {
    if (!this.config.features.selectAllAcrossPages) return false;
    return this.globalHeaderCheckboxState && this.matDataSource.data.length > 0;
  }

  get showSelectAllHyperlink(): boolean {
    if (!this.showSelectAllBanner) {
      return false;
    }
    return (
      this.totalCount > this.matDataSource.data.length &&
      !!this.paginator?.hasNextPage()
    );
  }

  private updatePageSizeOptions(): void {
    if (this.disablePageSize) {
      this.pageSizeOptions = [this.currentPageSize];
    } else {
      this.pageSizeOptions = [10, 25, 50];
    }
  }


  private updateEmptyMessage(): void {
    this.emptyMessage = this._dataSearched
      ? this.config?.messages?.noMatch ?? ''
      : this.config?.messages?.noSearch ?? '';
  }

  onPageChange(event: PageEvent): void {
    if (this.adapter.paginate) {
      this.adapter.paginate(event.pageIndex, event.pageSize);
    } else {
      this.currentPageIndex = event.pageIndex;
      this.currentPageSize = event.pageSize;
      if (this.disablePageSize) {
        this.updatePageSizeOptions();
      }
    }
    this.pageChange.emit(event);
    this.cdr.markForCheck();
  }

  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (!this.config.features.columnReorder) return;

    const reordered = reorderColumnsAfterHeaderDrop(
      event,
      this.visibleColumns,
      this.tableColumnProps
    );
    this.visibleColumns = reordered.visibleColumns;
    this.tableColumnProps = reordered.tableColumnProps;
    this.persistColumnPreferences();
    this.rebuildDisplayedColumns();
    this.syncTableServiceForCustomizeMenu();
    this.cdr.markForCheck();
  }

  onCellClick(row: TRow, column: ColumnConfig<TRow>): void {
    if (column.isHyperlink || this.config?.features?.rowClickOnAllCells) {
      this.rowClick.emit({
        row, column: String(column.key),
        linkRoute: column.linkRoute ?? ''
      });
    }
  }

  getCellValue(row: TRow, column: ColumnConfig<TRow>): string {
    if (column.displayFn) {
      return column.displayFn(row);
    }

    const value = row[column.key as keyof TRow];
    return value == null ? '' : String(value);
  }

  getCellClass(row: TRow, column: ColumnConfig<TRow>): string {
    return column.cellClassFn?.(row) ?? '';
  }

  isCellTooltipDisabled(row: TRow, column: ColumnConfig<TRow>): boolean {
    if (!column.showTooltip) {
      return true;
    }
    return !this.getCellValue(row, column).trim();
  }

  isCellHyperlink(row: TRow, column: ColumnConfig<TRow>): boolean {
    if (!column.isHyperlink) return false;

    if (column.hyperlinkCondition) {
      return column.hyperlinkCondition(row);
    }

    return true;
  }

  shouldShowDashboardShortcutIcon(column: ColumnConfig<TRow>): boolean {
    return !!column.showRowShortcutIcon;
  }

  onDashboardShortcutIconClick(row: TRow, column: ColumnConfig<TRow>): void {
    this.dashboardShortcutClick.emit({
      row,
      column: String(column.key),
      linkRoute: column.linkRoute
    });
  }

  onActionButtonClick(row: TRow, column: ColumnConfig<TRow>): void {
    this.actionButtonClick.emit({ row, column: String(column.key) });
  }

  onRowActionClick(row: TRow, action: string): void {
    this.rowActionClick.emit({ row, action });
  }

  isRowSelectionDisabled(row: TRow): boolean {
    return this.config.rowSelectionDisabledFn?.(row) ?? false;
  }

  getRowActions(row: TRow, column: ColumnConfig<TRow>): readonly string[] {
    return column.rowActionsFn?.(row) ?? [];
  }

  isRowActionDisabled(row: TRow, column: ColumnConfig<TRow>, action: string): boolean {
    return column.rowActionDisabledFn?.(row, action) ?? false;
  }

  onCustomizeColumnsClick(menuOverlay: HTMLElement): void {
    if (this.isOverlayOpen) {
      this.menuOverlayService.closeOverlay();
    } else {
      this.syncTableServiceForCustomizeMenu();
      this.menuOverlayService.openSearchOverlay(menuOverlay, this.config.storageKey);
    }
    this.isOverlayOpen = !this.isOverlayOpen;
  }

  private refreshColumnsFromTableService(): void {
    const menuColumns = [...this.tableService.customizeMenuColumns.value];
    const reordered = this.tableService.reorderedColumns.value.length
      ? this.tableService.reorderedColumns.value
      : this.columnPreferences.columnOrder;
    const visibleKeySet = new Set(
      menuColumns.filter(col => col.isSelected).map(col => col.key)
    );
    const orderedVisibleKeys = reordered.filter(
      key =>
        visibleKeySet.has(key) && key !== 'select' && key !== 'action'
    );
    menuColumns
      .filter(col => col.isSelected && !orderedVisibleKeys.includes(col.key))
      .forEach(col => orderedVisibleKeys.push(col.key));
    this.tableColumnProps = sortTableColumnPropsByVisibleOrder(
      menuColumns,
      orderedVisibleKeys
    );
    this.visibleColumns = orderedVisibleKeys
      .map(key => this.config.columns.find(col => String(col.key) === key))
      .filter((col): col is ColumnConfig<TRow> => !!col);
    this.persistColumnPreferences();
    this.rebuildDisplayedColumns();
    this.syncTableServiceForCustomizeMenu();
    this.cdr.markForCheck();
  }

  private persistColumnPreferences(): void {
    const visibleKeys = this.visibleColumns.map(col => String(col.key));
    saveGridColumnPreferences(
      this.localStorageService,
      this.config.storageKey,
      this.tableColumnProps,
      visibleKeys,
      this.config.features
    );
    this.columnPreferences = loadGridColumnPreferences(
      this.localStorageService,
      this.config.storageKey,
      this.config
    );
  }

  private syncTableServiceForCustomizeMenu(): void {
    this.tableService.setCustomizeMenuColumns(this.tableColumnProps);
    this.tableService.setTableDisplayedColumns(this.displayedColumns);
    this.tableService.setReorderedColumns(this.columnPreferences.columnOrder);
    this.tableService.setDefaultTableColumn(this.getDefaultLockedColumnKey());
  }

  private getDefaultLockedColumnKey(): string {
    if (this.config.defaultLockedColumn != null) {
      return String(this.config.defaultLockedColumn);
    }
    const hyperlinkColumn = this.config.columns.find(col => col.isHyperlink);
    if (hyperlinkColumn) {
      return String(hyperlinkColumn.key);
    }
    return String(this.config.columns[0]?.key ?? '');
  }

  private cacheSelectedRow(row: TRow): void {
    const key = row[this.config.rowIdField] as string | number;
    this.rootSelectionService.add(key as string, row);
  }

  private removeCachedRow(row: TRow): void {
    const key = row[this.config.rowIdField] as string | number;
    this.rootSelectionService.remove(key as string);
  }

  private buildRowLookupMap(): Map<string | number, TRow> {
    const keyToRow = new Map<string | number, TRow>();
    for (const row of this.matDataSource.data) {
      const key = row[this.config.rowIdField] as string | number;
      keyToRow.set(key, row);
      if (typeof key === 'number') {
        keyToRow.set(String(key), row);
      }
    }
    return keyToRow;
  }

 public getInfoIconTooltip(row: TRow, column: ColumnConfig<TRow>): string {
    return column.infoIconTooltipFn?.(row)?.trim() ?? '';
  }
}
